import { useState } from 'react'
import clsx from 'clsx'
import { HelpCircle } from 'react-feather'
import dayjs from 'dayjs'
import { LinearProgress } from '@material-ui/core'
import { Button, Card, Divider, Icon, Text } from '@/components'
import { AlertMessage } from 'app-components'
import Battery from '@/app/features/electric-vehicles/charging-history/Battery'
import GasSavingsDialog from '@/authenticated/components/GasSavingsDialog'
import { useNavigation } from '@/app/hooks'
import DateRangePicker from '@/app/components/DateRangePicker'
import FeatureFeedbackBanner from '@/app/components/FeatureFeedback'
import { PlugSession, PlugSessions } from '@/types/plugSessions'
import {
  formatQuarterHours,
  formatSessionDates,
  isLevel1Session,
  plugDurationHours,
} from './PlugSessionDetail'
import useViewModel from './useViewModel'

function formatCurrency(amount: number, currency: string) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(
    amount,
  )
}

// Multi-day disambiguation lives in the date row (formatSessionDates renders
// "November 10–12"); the session length is the Duration stat, so the time
// range stays plain
function formatTimeRange(start: string, end: string) {
  return `${dayjs(start).format('h:mm A')} – ${dayjs(end).format('h:mm A')}`
}

// Filter-chip date: "Nov 8" in the current year, "Nov 8, 2025" otherwise
function formatChipDate(date: string | null) {
  const d = dayjs(date)
  return d.isSame(dayjs(), 'year') ? d.format('MMM D') : d.format('MMM D, YYYY')
}

const MISSED_TARGET_HELP_URL =
  'https://optiwatt.zendesk.com/hc/en-us/articles/49246876530203-Why-isn-t-my-charge-limit-or-minimum-being-respected'

/**
 * The blue info banner (same treatment as DR events on the home page),
 * rendered under the stats row whenever the session missed its charge target.
 * A doomed session (analyzer: mathematically impossible to hit the limit in
 * the plugged-in time) coaches "plug in earlier" instead of the generic copy.
 */
function MissedTargetAlert({ session }: { session: PlugSession }) {
  const { charge_limit_doomed, charge_target_percent, ready_by_time } = session
  // Fold the limit and Ready By time into the doomed copy when known:
  // "…your charge limit of 80% by 6:00 AM."
  const limitPhrase = charge_target_percent
    ? ` of ${charge_target_percent}%${
        ready_by_time ? ` by ${ready_by_time}` : ''
      }`
    : ''
  return (
    <AlertMessage variant="info">
      {charge_limit_doomed
        ? `There wasn't enough time to hit your charge limit${limitPhrase}. `
        : "Your battery didn't hit its charge limit. "}
      <a
        href={MISSED_TARGET_HELP_URL}
        target="_blank"
        rel="noreferrer"
        className="underline underline-offset-2 cursor-pointer !text-inherit"
        // Inside a clickable card — don't also open the detail
        onClick={(event) => event.stopPropagation()}
      >
        Learn more.
      </a>
    </AlertMessage>
  )
}

function NoRatePlanAlert({ onAddRatePlan }: { onAddRatePlan?: () => void }) {
  return (
    <AlertMessage variant="info">
      {'To see what each plug session costs, '}
      <span
        className="underline underline-offset-2 cursor-pointer"
        onClick={onAddRatePlan}
      >
        add your electricity rate plan.
      </span>
    </AlertMessage>
  )
}

type SessionStatProps = {
  label: string
  children: React.ReactNode
}

function SessionStat({ label, children }: SessionStatProps) {
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      {/* Labels can wrap: on narrow screens wrapping beats overflowing the
          card, and min-w-0 lets the column shrink below its content */}
      <Text variant="body4">{label}</Text>
      {/* mt-auto bottom-aligns values across columns when one label wraps */}
      <Text variant="body1" className="tabular-nums mt-auto">
        {children}
      </Text>
    </div>
  )
}

type PlugSessionCardProps = {
  session: PlugSession
  showCosts: boolean
  onSelect?: (session: PlugSession) => void
}

function PlugSessionCard({
  session,
  showCosts,
  onSelect,
}: PlugSessionCardProps) {
  const {
    start_time,
    end_time,
    total_cost,
    energy_kwh,
    max_charge_rate_kw,
    currency,
    missed_charge_target,
  } = session

  // The detail view only adds information when the session has hourly
  // telemetry data — the chart matters on any plan type (flat-rate charging
  // can pause mid-session too). Without telemetry the page would just
  // repeat this card
  const hasDetail = (session.hourly_costs?.length ?? 0) > 0
  const clickable = showCosts && hasDetail && !!onSelect

  return (
    <Card
      fullWidth
      onClick={clickable ? () => onSelect(session) : undefined}
      // box-border: this app has no global border-box reset (preflight off), so
      // Card's w-full + p-6 overflows its parent without it
      className={clsx('box-border', clickable && 'cursor-pointer')}
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-themed-green-100 rounded-lg flex items-center justify-center shrink-0">
            <Icon name="BatteryCharging" size={20} color="green-700" />
          </div>
          <div className="flex flex-col gap-0.5 shrink-0">
            <Text variant="body1">{formatSessionDates(session)}</Text>
            <Text variant="body4" className="whitespace-nowrap">
              {formatTimeRange(start_time, end_time)}
            </Text>
          </div>
          {showCosts && total_cost !== null && (
            <div className="flex flex-1 items-center justify-end gap-1 min-w-0">
              <Text
                variant="subheader"
                className="whitespace-nowrap tabular-nums"
              >
                {formatCurrency(total_cost, currency)}
              </Text>
              {clickable && (
                <Icon name="ChevronRight" size={20} color="grey-500" />
              )}
            </div>
          )}
        </div>

        <Divider />

        {/* Charger-only sessions have no SoC (a wall charger can't read the
            car's battery) — hide the battery instead of an empty shell. The
            header row already shows the time range */}
        {session.start_battery_percent !== null &&
          session.end_battery_percent !== null && (
            <Battery charge={session} hideTimes bordered />
          )}

        {/* Natural-width columns spread across the row: equal thirds are a
            few px too narrow for the labels at the 335px design width.
            Charged For = Energy Used / Charge Rate: the active charging
            time, vs Plugged In which includes idle time. Charge Rate
            itself lives on the detail page now. */}
        <div className="flex gap-2 justify-between">
          <SessionStat label="Plugged In">
            {formatQuarterHours(plugDurationHours(start_time, end_time))}
          </SessionStat>
          {max_charge_rate_kw !== null && max_charge_rate_kw > 0 && (
            <SessionStat label="Charged For">
              {formatQuarterHours(energy_kwh / max_charge_rate_kw)}
            </SessionStat>
          )}
          <SessionStat label="Energy Used">{energy_kwh} kWh</SessionStat>
        </div>

        {/* L1 (120 V) charging often can't finish overnight regardless of
            behavior — the missed-limit coaching never applies */}
        {missed_charge_target && !isLevel1Session(session) && (
          <MissedTargetAlert session={session} />
        )}

        {/* Charger session correlated to a car charge — attribute it. The
            name is either the car's display name or "your {year} {make}
            {model}", composed by the BE */}
        {session.charged_vehicle_name && (
          <AlertMessage variant="info">
            {`This plug session was to charge ${session.charged_vehicle_name}.`}
          </AlertMessage>
        )}
      </div>
    </Card>
  )
}

/**
 * Summary card beside Total Spent: how many sessions the filtered range
 * holds and the total time plugged in across them, rounded to whole hours.
 */
function SessionsSummaryCard({ sessions }: { sessions: PlugSessions }) {
  // Average ACTIVE charging time per session (charge-record durations, not
  // plugged-in time) to the nearest half hour; 13 renders "13", not "13.0"
  // (JS numbers drop the trailing .0 on their own)
  const averageHours = sessions.count
    ? Math.round(sessions.total_active_charge_seconds / sessions.count / 1800) /
      2
    : 0
  return (
    <Card fullWidth className="box-border">
      {/* h-auto: same stretch fix as TotalSpentCard */}
      <div className="flex flex-col gap-1 h-auto items-center text-center">
        <Text variant="body1">Total Plug Sessions</Text>
        <Text variant="h1" className="tabular-nums">
          {sessions.count}
        </Text>
        <Text variant="body2">
          {`Avg. Charge Time: ${averageHours} hr${
            averageHours === 1 ? '' : 's'
          }`}
        </Text>
      </div>
    </Card>
  )
}

/**
 * Section header card: the range's total spend, plus savings vs. an equivalent
 * gas car. The info icon opens the same GasSavingsDialog used by the legacy
 * Total Spent card, so gas-car details stay editable in one place.
 */
function TotalSpentCard({
  sessions,
  vehicleId,
}: {
  sessions: PlugSessions
  vehicleId?: number
}) {
  const [isGasDialogOpen, setGasDialogOpen] = useState(false)

  return (
    <Card fullWidth className="box-border">
      {/* h-auto: Card's content wrapper is h-full, which would stretch this
          short content and leave dead space below the savings row */}
      <div className="flex flex-col gap-1 h-auto items-center text-center">
        <Text variant="body1">Total Spent</Text>
        <Text variant="h1" className="tabular-nums">
          {formatCurrency(sessions.total_cost ?? 0, sessions.currency)}
        </Text>
        {sessions.gas_savings !== null && vehicleId !== undefined && (
          <div className="flex items-center gap-1">
            <Text variant="body2" className="!text-themed-green-700">
              {`${formatCurrency(
                sessions.gas_savings,
                sessions.currency,
              )} saved vs. gas`}
            </Text>
            <button
              aria-label="Edit gas car details"
              onClick={() => setGasDialogOpen(true)}
              // No global preflight: strip the UA button chrome explicitly
              className="text-themed-grey-900 bg-transparent border-0 p-0 flex items-center"
            >
              <HelpCircle size={14} />
            </button>
          </div>
        )}
      </div>
      {/* Mount only while open — the dialog renders its own loading state
          otherwise, which would sit inside the card */}
      {isGasDialogOpen && vehicleId !== undefined && (
        <GasSavingsDialog
          open
          onClose={() => setGasDialogOpen(false)}
          onSubmit={() => setGasDialogOpen(false)}
          onSubmitPressed={() => setGasDialogOpen(false)}
          initialVehicleId={vehicleId}
        />
      )}
    </Card>
  )
}

export type PlugSessionsViewProps = {
  sessions: PlugSessions
  onPageChange: (page: number) => void
  startDate: string | null
  endDate: string | null
  onDateRangeChange: (start: string | null, end: string | null) => void
  isFiltered: boolean
  onResetDateRange: () => void
  onAddRatePlan?: () => void
  /** Needed by the gas-savings dialog to edit the right vehicle's conversion */
  vehicleId?: number
  /** Slot for the feature-feedback banner — injected by the page wrapper so
   *  this view (and its stories) stay free of redux/survey wiring */
  feedbackBanner?: React.ReactNode
}

export function PlugSessionsView({
  sessions,
  onPageChange,
  startDate,
  endDate,
  onDateRangeChange,
  isFiltered,
  onResetDateRange,
  onAddRatePlan,
  vehicleId,
  feedbackBanner,
}: PlugSessionsViewProps) {
  const navigation = useNavigation()
  const showCosts = sessions.has_rate_plan

  // The detail (L2) is a real route, not an inline swap — tapping a card
  // navigates away from the dashboard and back returns to it
  const onSelect = (session: PlugSession) =>
    navigation.push(`/plug-sessions/${session.id}/details`)

  const isEmpty = sessions.results.length === 0

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex items-center gap-2">
        <Text variant="h3">Plug Sessions</Text>
        <span className="bg-themed-purple-200 rounded-full px-2 py-0.5">
          <Text variant="body4" className="!text-themed-purple-500 font-bold">
            New
          </Text>
        </span>
      </div>

      {feedbackBanner}

      {/* Hide the picker only for a genuinely session-less account — if a
          user's own filter emptied the list, hiding it would trap them */}
      {(!isEmpty || isFiltered) && (
        <DateRangePicker
          id="plug-sessions-date-range"
          startDate={startDate}
          endDate={endDate}
          onChange={onDateRangeChange}
        />
      )}

      {/* Removable filter chip: shows the active range, X clears it */}
      {isFiltered && (
        <div className="flex justify-end -mt-1">
          <button
            aria-label="Clear date filter"
            onClick={onResetDateRange}
            className="flex items-center gap-1.5 bg-themed-grey-900 rounded-full px-3 py-1 border-0"
          >
            <Text variant="body4" className="!text-themed-white">
              {`${formatChipDate(startDate)} – ${formatChipDate(endDate)}`}
            </Text>
            <Icon name="X" size={14} color="white" />
          </button>
        </div>
      )}

      {/* The rate-plan CTA shows even before the first session (AC 2) —
          it's a good prompt while they wait */}
      {!showCosts && <NoRatePlanAlert onAddRatePlan={onAddRatePlan} />}

      {!isEmpty && (
        <div
          className={clsx(
            'grid gap-4',
            // Stacked on mobile, side by side from sm up — and without a
            // rate plan there's no Total Spent, so the summary card keeps
            // the full row at every width
            showCosts && sessions.total_cost !== null
              ? 'grid-cols-1 sm:grid-cols-2'
              : 'grid-cols-1',
          )}
        >
          {showCosts && sessions.total_cost !== null && (
            <TotalSpentCard sessions={sessions} vehicleId={vehicleId} />
          )}
          <SessionsSummaryCard sessions={sessions} />
        </div>
      )}

      {isEmpty && (
        <Card fullWidth className="box-border">
          <div className="flex flex-col items-center gap-2 py-4 text-center">
            <div className="w-10 h-10 bg-themed-grey-100 rounded-lg flex items-center justify-center">
              <Icon name="BatteryCharging" size={22} color="grey-500" />
            </div>
            {isFiltered ? (
              <>
                <Text variant="body1">No plug sessions to show</Text>
                <Text variant="body4">
                  Remove or change the date filters above to see more plug
                  sessions
                </Text>
              </>
            ) : (
              <>
                <Text variant="body1">No plug sessions yet</Text>
                <Text variant="body4">
                  Plug in your car to see your first plug session
                </Text>
              </>
            )}
          </div>
        </Card>
      )}

      {/* Two columns on bigger screens, matching the legacy card layout */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {sessions.results.map((session) => (
          <PlugSessionCard
            key={session.id}
            session={session}
            showCosts={showCosts}
            onSelect={onSelect}
          />
        ))}
      </div>

      {/* Hidden once the last page is loaded — pages accumulate in the
          view model, so page_number tracks the deepest page fetched */}
      {sessions.page_number < sessions.total_pages && (
        <Button
          id="plug-sessions-load-more"
          variant="primary"
          onClick={() => onPageChange(sessions.page_number + 1)}
        >
          Load more sessions
        </Button>
      )}
    </div>
  )
}

export default function PlugSessionsPage({
  vehicleId,
  chargerId,
}: {
  vehicleId?: number
  chargerId?: number
}) {
  const navigation = useNavigation()
  const {
    sessions,
    isLoading,
    hasError,
    onPageChange,
    startDate,
    endDate,
    onDateRangeChange,
    isFiltered,
    onResetDateRange,
  } = useViewModel({ vehicleId, chargerId })

  if (hasError && !sessions) {
    return (
      <div className="flex flex-col gap-4 w-full">
        <Text variant="h3">Plug Sessions</Text>
        <AlertMessage variant="warning">
          We couldn&apos;t load your plug sessions. Please try again in a
          moment.
        </AlertMessage>
      </div>
    )
  }

  if (isLoading || !sessions) {
    return <LinearProgress />
  }

  return (
    <PlugSessionsView
      sessions={sessions}
      onPageChange={onPageChange}
      startDate={startDate}
      endDate={endDate}
      onDateRangeChange={onDateRangeChange}
      isFiltered={isFiltered}
      onResetDateRange={onResetDateRange}
      onAddRatePlan={() => navigation.push('/connect-utility/select')}
      vehicleId={vehicleId}
      feedbackBanner={
        <FeatureFeedbackBanner
          surveyName="Plug Sessions Feedback 1"
          question="What do you think about this updated feature?"
        />
      }
    />
  )
}
